import { useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const PIE_COLORS = [
  '#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#6b7280',
  '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16', '#a855f7',
  '#e11d48', '#0ea5e9', '#eab308', '#22c55e', '#d946ef', '#64748b',
  '#fb923c', '#2dd4bf'
];

interface FacetBucket {
  value: string;
  count: number;
}

interface PieChartPopupProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  data: FacetBucket[];
  selected: string[];
  onSelect: (value: string) => void;
}

export const PieChartPopup = ({ isOpen, onClose, title, data, selected, onSelect }: PieChartPopupProps) => {
  const [search, setSearch] = useState('');

  if (!isOpen) return null;

  const sorted = [...data].sort((a, b) => b.count - a.count);
  const filtered = sorted.filter(d => d.value.toLowerCase().includes(search.toLowerCase()));
  const pieData = filtered.slice(0, 200).map(b => ({ name: b.value, value: b.count }));

  return (
    <div data-component="PieChartPopup" className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
            aria-label="Close popup"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Pie chart */}
          <div className="flex-1 p-4 flex items-center justify-center min-w-0">
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={160}
                  innerRadius={60}
                  onClick={(_, idx) => {
                    const item = pieData[idx];
                    if (item) onSelect(item.name);
                  }}
                  cursor="pointer"
                >
                  {pieData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={PIE_COLORS[i % PIE_COLORS.length]}
                      stroke={selected.includes(entry.name) ? '#1d4ed8' : '#fff'}
                      strokeWidth={selected.includes(entry.name) ? 3 : 1}
                      opacity={selected.length > 0 && !selected.includes(entry.name) ? 0.4 : 1}
                    />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any, name: any) => [Number(value).toLocaleString(), name ?? '']} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legend / list */}
          <div className="w-64 border-l border-gray-200 flex flex-col">
            <div className="p-2 border-b border-gray-100">
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
                aria-label="Search items"
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {filtered.slice(0, 200).map((item, i) => {
                const isSelected = selected.includes(item.value);
                return (
                  <button
                    key={item.value}
                    onClick={() => onSelect(item.value)}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-blue-50 font-medium' : ''
                    }`}
                  >
                    <span
                      className="w-3 h-3 rounded-sm flex-shrink-0"
                      style={{
                        backgroundColor: PIE_COLORS[i % PIE_COLORS.length],
                        opacity: selected.length > 0 && !isSelected ? 0.4 : 1
                      }}
                    />
                    <span className="flex-1 text-xs text-gray-700 truncate">{item.value}</span>
                    <span className="text-xs text-gray-400">{item.count.toLocaleString()}</span>
                    {isSelected && (
                      <span className="text-xs text-blue-600">✓</span>
                    )}
                  </button>
                );
              })}
            </div>
            {selected.length > 0 && (
              <div className="p-2 border-t border-gray-100 text-xs text-gray-500">
                {selected.length} selected — click to deselect
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
